import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import CustomDropdown from "../../../components/CustomDropdown";
import IbReferralQRCode, { getIbReferralQrImageSrc } from "./IbReferralQRCode";
import { useToast } from "../../../contexts/ToastContext";
import {
  getInformationAccountLinkPrefix,
  normalizeInviteAccountIdDigits,
  buildInformationAccountInviteUrl,
  copyTextToClipboard,
} from "../../../utils/ibReferralLink";
import {
  getIbDashboard,
  getResponseCode,
  getTradingAccounts,
  getTradingInfos,
  updateTradingInfos,
} from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import { AccountType } from "../../../utils/enums";
import { formatIbClientName } from "../../../utils/ibClientDisplayName";
import { extractEmailLoose } from "../../../utils/extractEmailLoose";
import { useMediaMaxWidth } from "../../../hooks/useMediaMaxWidth";
import "../../css/List.css";
import "../../css/IbDashboard.css";

const RECENT_PAGE_SIZE = 4;

const buildDistributionConicGradient = (segments, fromDeg = -90) => {
  let acc = 0;
  const stops = segments.map((s) => {
    const startDeg = acc * 3.6;
    acc += s.pct;
    const endDeg = acc * 3.6;
    return `${s.color} ${startDeg}deg ${endDeg}deg`;
  });
  return `conic-gradient(from ${fromDeg}deg, ${stops.join(", ")})`;
};

const KPI_CARDS = [
  { id: "joinedToday", labelKey: "ib_kpi_new_traders", iconClass: "fa-user-plus" },
  { id: "totalClients", labelKey: "ib_kpi_total_clients", iconClass: "fa-users" },
  { id: "totalCommission", labelKey: "ib_kpi_total_commission", iconClass: "fa-wallet" },
  { id: "totalSubIbs", labelKey: "ib_kpi_total_sub_ibs", iconClass: "fa-sitemap" },
];

const DIST_COLORS = ["#22c55e", "#facc15", "#38bdf8", "#9ca3af", "#a78bfa", "#fb7185"];
const DIST_TYPE_COLORS = {
  LARGE: "#EF476F",
  MINI: "#06D6A0",
  STANDARD: "#FFD166",
};

const getDistributionColor = (rawKey, idx) => {
  const normalized = String(rawKey || "")
    .replace(/[\s-]+/g, "_")
    .toUpperCase()
    .trim();
  return DIST_TYPE_COLORS[normalized] || DIST_COLORS[idx % DIST_COLORS.length];
};

const avatarInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  return (parts[0]?.slice(0, 2) || "?").toUpperCase();
};

const formatEquity = (n) =>
  `$${Number(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function isMissing(v) {
  return (
    v === null ||
    v === undefined ||
    (typeof v === "string" && String(v).trim() === "") ||
    (typeof v === "number" && Number.isNaN(v))
  );
}

function disp(v, fallback = "—") {
  if (isMissing(v)) return fallback;
  if (typeof v === "number") return v.toLocaleString("en-US");
  return String(v);
}

function parseNumber(raw) {
  if (isMissing(raw)) return null;
  const n = typeof raw === "string" ? Number(raw.trim()) : Number(raw);
  return Number.isFinite(n) ? n : null;
}

function extractLeverageNumber(leverageValue) {
  if (leverageValue == null || leverageValue === "") return null;
  let value = leverageValue;
  if (typeof value === "object" && value !== null) {
    value = value.value || value.label || value;
  }
  if (typeof value === "string" && value.includes(":")) {
    const parts = value.split(":");
    return parts[parts.length - 1].trim();
  }
  return value.toString().trim();
}

function extractTradingAccountsList(decryptedData) {
  if (!decryptedData) return [];
  if (decryptedData.DATA && Array.isArray(decryptedData.DATA)) return decryptedData.DATA;
  if (decryptedData.data && Array.isArray(decryptedData.data)) return decryptedData.data;
  if (Array.isArray(decryptedData)) return decryptedData;
  return [];
}

function mapBrokerTradingAccountsToOptions(accounts) {
  if (!Array.isArray(accounts)) return [];
  const seen = new Set();
  const options = [];
  for (const account of accounts) {
    const accountInfo = account.account_information || account.accountInformation || {};
    const tradingIdRaw =
      accountInfo.TRADING_ID ||
      account.TRADING_ID ||
      account.trading_id ||
      account.ID ||
      account.id;
    const digits = normalizeInviteAccountIdDigits(tradingIdRaw);
    if (!digits || seen.has(digits)) continue;
    seen.add(digits);
    const nick = String(
      account.NICK ||
        account.NICK_NAME ||
        account.nick ||
        accountInfo.NAME ||
        accountInfo.name ||
        ""
    ).trim();
    options.push({
      value: digits,
      label: nick ? `${digits} — ${nick}` : digits,
    });
  }
  return options.sort((a, b) =>
    a.value.localeCompare(b.value, undefined, { numeric: true })
  );
}

function formatRelativeTime(t, isoLike) {
  if (isMissing(isoLike)) return "—";
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return "—";
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) return t("ib_activity_hours_ago", { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  return t("ib_activity_days_ago", { count: diffDays });
}

const formatRecentActivity = (t, row) => {
  if (row.activityAt) return formatRelativeTime(t, row.activityAt);
  return "—";
};

function formatJoinedAt(t, isoLike) {
  if (isMissing(isoLike)) return "—";
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return "—";
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) return t("ib_joined_today");
  const diffDays = Math.floor(diffHours / 24);
  return t("ib_joined_days_ago", { count: diffDays });
}

function normalizeRecentClient(raw, idx) {
  if (!raw) return null;
  const id = raw.CLIENT_ID ?? raw.ID ?? raw.USER_ID ?? idx;
  const statusRaw = raw.STATUS ?? raw.USER_STATUS ?? raw.status;
  const status =
    String(statusRaw || "")
      .toLowerCase()
      .trim() || "inactive";
  return {
    id: String(id),
    name: raw.NICK_NAME ?? raw.FULL_NAME ?? raw.NAME ?? "—",
    clientNum: raw.CLIENT_NUMBER ?? raw.CLIENT_NUM ?? raw.CLIENT_CODE ?? raw.CODE ?? id,
    status,
    accountType: raw.ACCOUNT_TYPE ?? raw.ACCOUNT ?? raw.TIER ?? "—",
    equity: parseNumber(raw.EQUITY) ?? 0,
    activityAt: raw.LAST_ACTIVITY_AT ?? raw.LAST_ACTIVITY ?? raw.UPDATED_AT ?? null,
    email:
      raw.EMAIL ??
      raw.email ??
      raw.USER_EMAIL ??
      raw.user_email ??
      raw.user_information?.EMAIL ??
      raw.userInformation?.EMAIL ??
      extractEmailLoose(raw) ??
      null,
  };
}

function normalizeNewClient(raw, idx, emailFallback = null) {
  if (!raw) return null;
  const id = raw.CLIENT_ID ?? raw.ID ?? raw.USER_ID ?? idx;
  const name = raw.NICK_NAME ?? raw.FULL_NAME ?? raw.NAME ?? "—";
  const email =
    raw.EMAIL ??
    raw.email ??
    raw.USER_EMAIL ??
    raw.user_email ??
    raw.MAIL ??
    raw.user_information?.EMAIL ??
    raw.userInformation?.EMAIL ??
    extractEmailLoose(raw) ??
    emailFallback ??
    null;
  return {
    id: String(id),
    name,
    initials: raw.INITIALS ?? avatarInitials(name),
    tier: String(raw.TIER || "").toLowerCase() || "std",
    joinedAt: raw.JOINED_AT ?? raw.CREATED_AT ?? raw.REGISTER_DATE ?? null,
    email,
  };
}

function normalizeDistribution(rawDist) {
  const dist = rawDist && typeof rawDist === "object" ? rawDist : {};
  const keys = Object.keys(dist).filter((k) => k !== "TOTAL_ACCOUNTS");
  const segments = [];
  keys.forEach((k, i) => {
    const entry = dist[k] || {};
    const pct = parseNumber(entry.PERCENT) ?? 0;
    const count = parseNumber(entry.COUNT) ?? 0;
    // Skip empty buckets so Mini-only data is not drawn as Large/Mini/Standard at 0% or 33%.
    if (pct <= 0 && count <= 0) return;
    segments.push({
      id: k,
      label: k.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()),
      color: getDistributionColor(k, i),
      pct,
      count,
    });
  });
  const totalCount = segments.reduce((acc, s) => acc + Math.max(0, Number(s.count) || 0), 0);

  // Prefer counts when present so percentages match the actual clients, not empty sibling keys.
  if (totalCount > 0) {
    return segments
      .map((s) => ({
        ...s,
        pct: Number(((Math.max(0, Number(s.count) || 0) / totalCount) * 100).toFixed(2)),
      }))
      .sort((a, b) => (b.pct || 0) - (a.pct || 0) || String(a.id).localeCompare(String(b.id)));
  }

  const totalPct = segments.reduce((acc, s) => acc + (s.pct || 0), 0);
  if (totalPct <= 0) return [];

  segments.sort((a, b) => (b.pct || 0) - (a.pct || 0) || String(a.id).localeCompare(String(b.id)));
  return segments;
}

const normalizeClientTypeKey = (rawType) => {
  const normalized = String(rawType || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (!normalized) return null;
  if (normalized === "STD") return "STANDARD";
  if (normalized === "LARGE" || normalized === "MINI" || normalized === "STANDARD") return normalized;
  return null;
};

const buildDistributionFromClients = (dashboardData) => {
  const recentRaw = Array.isArray(dashboardData?.RECENT_CLIENTS) ? dashboardData.RECENT_CLIENTS : [];
  const newRaw = Array.isArray(dashboardData?.NEW_CLIENTS) ? dashboardData.NEW_CLIENTS : [];
  const all = [...recentRaw, ...newRaw];
  if (!all.length) return [];

  const byClientId = new Map();
  all.forEach((raw, idx) => {
    const id = String(raw?.CLIENT_ID ?? raw?.ID ?? raw?.USER_ID ?? `row-${idx}`);
    if (!byClientId.has(id)) byClientId.set(id, raw);
  });

  const counts = new Map();
  for (const raw of byClientId.values()) {
    const typeRaw =
      raw?.ACCOUNT_TYPE ??
      raw?.TIER ??
      raw?.accountType ??
      raw?.tier ??
      null;
    const typeKey = normalizeClientTypeKey(typeRaw) || "UNCLASSIFIED";
    counts.set(typeKey, (counts.get(typeKey) || 0) + 1);
  }

  const total = [...counts.values()].reduce((sum, n) => sum + n, 0);
  if (!total) return [];

  const segments = [...counts.entries()].map(([typeKey, count], idx) => ({
    id: typeKey,
    label: typeKey.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()),
    color: getDistributionColor(typeKey, idx),
    pct: Number(((count / total) * 100).toFixed(2)),
    count,
  }));

  segments.sort((a, b) => (b.pct || 0) - (a.pct || 0) || String(a.id).localeCompare(String(b.id)));
  return segments;
};

const IbDashboard = () => {
  const { t } = useTranslation("mobile");
  const { success: showSuccess, warning: showWarning } = useToast();
  const navigate = useNavigate();
  const isMobileLayout = useMediaMaxWidth(768);
  const [user, setUser] = useState(null);
  const [dashboardReferralCode, setDashboardReferralCode] = useState("");
  const [dashboardReferralBrokerIdDigits, setDashboardReferralBrokerIdDigits] = useState("");
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [inviteAccountIdDigits, setInviteAccountIdDigits] = useState("");
  const [editingInviteId, setEditingInviteId] = useState(false);
  const [savingInviteId, setSavingInviteId] = useState(false);
  const [inviteBrokerIdInvalid, setInviteBrokerIdInvalid] = useState(false);
  const [brokerAccountOptions, setBrokerAccountOptions] = useState([]);
  const [brokerAccountsLoading, setBrokerAccountsLoading] = useState(false);
  const [profileReferalBrokerDigits, setProfileReferalBrokerDigits] = useState("");
  const [recentSearch, setRecentSearch] = useState("");
  const [recentPage, setRecentPage] = useState(1);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData");
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch (_) {
      // ignore
    }
  }, []);

  useEffect(() => {
    const handleIbDashboardRefresh = () => {
      setRefreshTick((prev) => prev + 1);
    };
    window.addEventListener("ib-dashboard:refresh", handleIbDashboardRefresh);
    return () => window.removeEventListener("ib-dashboard:refresh", handleIbDashboardRefresh);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setDashboardLoading(true);
        const [ibRes, tradingRes] = await Promise.all([getIbDashboard(), getTradingInfos()]);
        if (cancelled) return;

        const decrypted = ibRes?.decryptedData ?? ibRes;
        const data = decrypted?.DATA ?? decrypted?.data ?? decrypted ?? null;
        setDashboardData(data);

        const tradingData = tradingRes?.decryptedData || tradingRes?.data;
        const profileBrokerDigits = normalizeInviteAccountIdDigits(
          tradingData?.DATA?.REFERAL_BROKER ?? tradingData?.DATA?.referal_broker
        );
        if (profileBrokerDigits) {
          setProfileReferalBrokerDigits(profileBrokerDigits);
          setDashboardReferralBrokerIdDigits(profileBrokerDigits);
        }

        const referralBrokerRaw =
          data?.REFERRAL_BROKER ??
          data?.REFERAL_BROKER ??
          data?.REFERRAL_BROKER_ID ??
          data?.BROKER_ID ??
          decrypted?.DATA?.REFERRAL_BROKER ??
          decrypted?.DATA?.REFERAL_BROKER ??
          "";
        const normalizedBrokerDigits =
          profileBrokerDigits || normalizeInviteAccountIdDigits(referralBrokerRaw);
        if (normalizedBrokerDigits) {
          setDashboardReferralBrokerIdDigits(normalizedBrokerDigits);
        }

        const referralRaw =
          decrypted?.DATA?.REFERRAL_CODE ??
          decrypted?.data?.REFERRAL_CODE ??
          decrypted?.DATA?.referral_code ??
          decrypted?.data?.referral_code ??
          decrypted?.DATA?.DATA?.REFERRAL_CODE ??
          decrypted?.data?.DATA?.REFERRAL_CODE ??
          "";
        const normalizedReferralDigits = normalizeInviteAccountIdDigits(referralRaw);
        if (normalizedReferralDigits) {
          setDashboardReferralCode(normalizedReferralDigits);
        }

        const inviteId =
          profileBrokerDigits || normalizedBrokerDigits || normalizedReferralDigits || "";
        if (inviteId && !editingInviteId) {
          setInviteAccountIdDigits(inviteId);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("[IbDashboard] dashboard load failed:", err);
        }
      } finally {
        if (!cancelled) setDashboardLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  useEffect(() => {
    if (!editingInviteId) return undefined;
    let cancelled = false;
    (async () => {
      setBrokerAccountsLoading(true);
      try {
        const response = await getTradingAccounts(AccountType.BROKER, 200, 1);
        if (cancelled) return;
        const decrypted = response?.decryptedData || response?.data;
        const accounts = extractTradingAccountsList(decrypted);
        setBrokerAccountOptions(mapBrokerTradingAccountsToOptions(accounts));
      } catch (err) {
        if (!cancelled) {
          console.error("[IbDashboard] load broker accounts failed:", err);
          setBrokerAccountOptions([]);
        }
      } finally {
        if (!cancelled) setBrokerAccountsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editingInviteId]);

  useEffect(() => {
    setInviteAccountIdDigits((prev) =>
      editingInviteId
        ? prev
        : prev ||
            profileReferalBrokerDigits ||
            dashboardReferralBrokerIdDigits ||
            dashboardReferralCode
    );
  }, [
    profileReferalBrokerDigits,
    dashboardReferralCode,
    dashboardReferralBrokerIdDigits,
    editingInviteId,
  ]);

  const informationAccountPrefixDisplay = useMemo(
    () => String(getInformationAccountLinkPrefix() || "").replace(/\/$/, ""),
    []
  );

  const inviteUrl = useMemo(
    () => buildInformationAccountInviteUrl(inviteAccountIdDigits),
    [inviteAccountIdDigits]
  );
  const hasInviteAccountId = useMemo(
    () => Boolean(normalizeInviteAccountIdDigits(inviteAccountIdDigits)),
    [inviteAccountIdDigits]
  );

  const selectedBrokerOption = useMemo(() => {
    const id = normalizeInviteAccountIdDigits(inviteAccountIdDigits);
    if (!id) return null;
    return (
      brokerAccountOptions.find((option) => option.value === id) || {
        value: id,
        label: id,
      }
    );
  }, [inviteAccountIdDigits, brokerAccountOptions]);

  const distributionSegments = useMemo(() => {
    const rawDistribution =
      dashboardData?.ACCOUNT_TYPE_DISTRIBUTION ||
      dashboardData?.ACCOUNT_DISTRIBUTION ||
      dashboardData?.DISTRIBUTION?.ACCOUNT_TYPE_DISTRIBUTION ||
      dashboardData?.STATISTICS?.ACCOUNT_TYPE_DISTRIBUTION ||
      null;

    const normalized = normalizeDistribution(rawDistribution);
    if (normalized.length > 0) return normalized;

    const derivedFromClients = buildDistributionFromClients(dashboardData);
    if (derivedFromClients.length > 0) return derivedFromClients;

    const totalClientsFromKpi = parseNumber(dashboardData?.KPIS?.TOTAL_CLIENTS?.VALUE) ?? 0;
    const recentClientsCount = Array.isArray(dashboardData?.RECENT_CLIENTS)
      ? dashboardData.RECENT_CLIENTS.length
      : 0;
    const newClientsCount = Array.isArray(dashboardData?.NEW_CLIENTS)
      ? dashboardData.NEW_CLIENTS.length
      : 0;
    const fallbackCount = Math.max(totalClientsFromKpi, recentClientsCount, newClientsCount, 0);

    // Fallback: if clients exist but server omitted distribution details,
    // still show a 100% single-slice distribution instead of hiding the card.
    if (fallbackCount > 0) {
      return [
        {
          id: "UNCLASSIFIED",
          label: "Unclassified",
          color: getDistributionColor("UNCLASSIFIED", 0),
          pct: 100,
          count: fallbackCount,
        },
      ];
    }

    return [];
  }, [dashboardData]);

  const distributionGradient = useMemo(() => {
    if (!distributionSegments.length) return "conic-gradient(from -90deg, #e5e7eb 0deg 360deg)";
    return buildDistributionConicGradient(distributionSegments, -90);
  }, [distributionSegments]);

  const distributionRows = useMemo(() => {
    if (!distributionSegments.length) return [];
    const perRow = 2;
    const rows = [];
    for (let i = 0; i < distributionSegments.length; i += perRow) {
      rows.push(distributionSegments.slice(i, i + perRow));
    }
    return rows;
  }, [distributionSegments]);
  const hasDistributionData = distributionSegments.length > 0;

  const filteredRecent = useMemo(() => {
    const apiRecentRaw = Array.isArray(dashboardData?.RECENT_CLIENTS) ? dashboardData.RECENT_CLIENTS : [];
    const apiRecent = apiRecentRaw.map((r, i) => normalizeRecentClient(r, i)).filter(Boolean);
    const q = recentSearch.trim().toLowerCase();
    if (!q) return apiRecent;
    return apiRecent.filter(
      (r) =>
        String(r.name || "").toLowerCase().includes(q) ||
        String(r.clientNum || "").includes(q) ||
        String(r.accountType || "").toLowerCase().includes(q)
    );
  }, [recentSearch, dashboardData]);

  useEffect(() => {
    setRecentPage(1);
  }, [recentSearch]);

  const recentTotalEntries = filteredRecent.length;
  const recentTotalPages = Math.max(1, Math.ceil(recentTotalEntries / RECENT_PAGE_SIZE) || 1);

  useEffect(() => {
    if (recentPage > recentTotalPages) setRecentPage(recentTotalPages);
  }, [recentPage, recentTotalPages]);

  const paginatedRecent = useMemo(() => {
    const start = (recentPage - 1) * RECENT_PAGE_SIZE;
    return filteredRecent.slice(start, start + RECENT_PAGE_SIZE);
  }, [filteredRecent, recentPage]);

  const recentShowingFrom =
    recentTotalEntries === 0 ? 0 : (recentPage - 1) * RECENT_PAGE_SIZE + 1;
  const recentShowingTo =
    recentTotalEntries === 0 ? 0 : Math.min(recentPage * RECENT_PAGE_SIZE, recentTotalEntries);

  const paginationPages = useMemo(() => {
    const last = recentTotalPages;
    if (last <= 5) return Array.from({ length: last }, (_, i) => i + 1);
    const cur = recentPage;
    const set = new Set([1, 2, 3, last, cur, cur - 1, cur + 1]);
    return [...set].filter((n) => n >= 1 && n <= last).sort((a, b) => a - b);
  }, [recentPage, recentTotalPages]);

  const handleCopyReferral = async () => {
    const id = normalizeInviteAccountIdDigits(inviteAccountIdDigits);
    if (!id) {
      showWarning(t("ib_referral_copied_title"), t("ib_invite_id_required"));
      return;
    }
    const url = buildInformationAccountInviteUrl(id);
    const ok = await copyTextToClipboard(url);
    if (ok) {
      showSuccess(t("ib_referral_copied_title"), t("ib_referral_copied_message"));
    } else {
      showWarning(t("ib_referral_copied_title"), t("ib_referral_copy_failed"));
    }
  };

  const handleDownloadQrCode = async () => {
    const id = normalizeInviteAccountIdDigits(inviteAccountIdDigits);
    if (!id) {
      showWarning(
        t("ib_referral_copied_title", { defaultValue: "Referral Link" }),
        t("ib_invite_id_required", { defaultValue: "Account number is required." })
      );
      return;
    }
    try {
      const qrSrc = getIbReferralQrImageSrc(inviteUrl, 512);
      const response = await fetch(qrSrc, { method: "GET" });
      if (!response.ok) {
        throw new Error(`Failed to download QR image. HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = `ib-referral-qr-${id}.png`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(blobUrl);
      showSuccess(
        t("ib_referral_copied_title", { defaultValue: "Referral Link" }),
        t("ib_qr_downloaded_message", { defaultValue: "QR code downloaded." })
      );
    } catch (error) {
      console.error("[IbDashboard] QR code download failed:", error);
      showWarning(
        t("ib_referral_copied_title", { defaultValue: "Referral Link" }),
        t("ib_qr_download_failed", { defaultValue: "Unable to download QR code." })
      );
    }
  };

  const finishEditInviteId = async () => {
    const normalized = normalizeInviteAccountIdDigits(inviteAccountIdDigits);
    if (!normalized) {
      setInviteBrokerIdInvalid(true);
      return;
    }

    if (brokerAccountOptions.length === 0) {
      showWarning(
        t("ib_referral_copied_title"),
        t("ib_no_broker_accounts", { defaultValue: "No broker accounts available." })
      );
      return;
    }

    if (!brokerAccountOptions.some((option) => option.value === normalized)) {
      setInviteBrokerIdInvalid(true);
      return;
    }

    const savedDigits =
      profileReferalBrokerDigits ||
      dashboardReferralBrokerIdDigits ||
      dashboardReferralCode ||
      "";
    if (normalized === savedDigits) {
      setInviteAccountIdDigits(normalized);
      setInviteBrokerIdInvalid(false);
      setEditingInviteId(false);
      return;
    }

    setSavingInviteId(true);
    setInviteBrokerIdInvalid(false);
    try {
      const tradingResponse = await getTradingInfos();
      const tradingData = tradingResponse?.decryptedData || tradingResponse?.data;
      const tradingCode = tradingData?.CODE ?? getResponseCode(tradingResponse);
      if (tradingCode !== AppConstants.responseOk) {
        showWarning(
          t("ib_referral_copied_title"),
          tradingData?.MESSAGE || t("trading_info_fetch_failed")
        );
        return;
      }

      const leverageNumber = extractLeverageNumber(
        tradingData?.DATA?.LEVERAGE ?? tradingData?.DATA?.leverage
      );
      if (!leverageNumber) {
        showWarning(t("ib_referral_copied_title"), t("leverage_required"));
        return;
      }

      const updateResponse = await updateTradingInfos(normalized, leverageNumber);
      const updateResult = updateResponse?.decryptedData || updateResponse?.data;
      const updateCode = updateResult?.CODE ?? getResponseCode(updateResponse);
      if (updateCode === AppConstants.responseOk) {
        setInviteAccountIdDigits(normalized);
        setProfileReferalBrokerDigits(normalized);
        setDashboardReferralBrokerIdDigits(normalized);
        setInviteBrokerIdInvalid(false);
        setEditingInviteId(false);
        showSuccess(t("ib_referral_copied_title"), t("trading_info_updated_successfully"));
      } else {
        showWarning(
          t("ib_referral_copied_title"),
          updateResult?.MESSAGE || t("trading_info_update_failed")
        );
      }
    } catch (error) {
      console.error("[IbDashboard] save invite broker id failed:", error);
      showWarning(t("ib_referral_copied_title"), t("trading_info_update_failed"));
    } finally {
      setSavingInviteId(false);
    }
  };

  const handleViewRecentClientAccounts = (row) => {
    const userId = row?.id;
    if (isMissing(userId)) return;
    navigate(`/pages/ibDashboard/client-accounts/${userId}`, {
      state: { clientName: disp(row?.name) },
    });
  };

  const kpiCards = useMemo(() => {
    const kpis = dashboardData?.KPIS || {};
    const joinedToday = kpis.JOINED_TODAY || {};
    const totalClients = kpis.TOTAL_CLIENTS || {};
    const totalCommission = kpis.TOTAL_COMMISSION || {};
    const totalSubIbs = kpis.TOTAL_SUB_IBS || {};

    const map = {
      joinedToday: {
        valueMain: disp(joinedToday.VALUE, "—"),
        valueSub: null,
      },
      totalClients: {
        valueMain: disp(totalClients.VALUE, "—"),
        valueSub: null,
      },
      totalCommission: {
        valueMain: totalCommission.VALUE != null ? `${String(totalCommission.CURRENCY || "").trim() ? "" : "$"}${disp(totalCommission.VALUE, "—")}` : "—",
        valueSub: null,
      },
      totalSubIbs: {
        valueMain: disp(totalSubIbs.VALUE, "—"),
        valueSub: null,
      },
    };

    return KPI_CARDS.map((cfg) => ({
      ...cfg,
      ...(map[cfg.id] || { valueMain: "—", valueSub: null }),
    }));
  }, [dashboardData]);

  const renderIbSkeleton = (className = "") => (
    <span className={`ib-dashboard-skeleton ${className}`.trim()} aria-hidden />
  );

  const newClients = useMemo(() => {
    const raw = Array.isArray(dashboardData?.NEW_CLIENTS) ? dashboardData.NEW_CLIENTS : [];
    const recentRaw = Array.isArray(dashboardData?.RECENT_CLIENTS) ? dashboardData.RECENT_CLIENTS : [];
    const recentNorm = recentRaw.map((r, i) => normalizeRecentClient(r, i)).filter(Boolean);
    const emailById = new Map(recentNorm.map((r) => [String(r.id), r.email]).filter(([, e]) => !isMissing(e)));
    return raw
      .map((r, i) => {
        const rid = String(r?.CLIENT_ID ?? r?.ID ?? r?.USER_ID ?? i);
        return normalizeNewClient(r, i, emailById.get(rid) || null);
      })
      .filter(Boolean);
  }, [dashboardData]);

  return (
    <div className="ib-dashboard-page">
      <Sidebar user={user} />
      <div className="ib-dashboard-main">
        <div className="container-fluid px-0 w-100 ib-dashboard-main-inner">
        <div className="ib-dashboard-kpi-grid">
          {kpiCards.map((kpi) => (
            <article key={kpi.id} className="ib-dashboard-kpi-card">
              <div className="ib-dashboard-kpi-top">
                <div className="ib-dashboard-kpi-icon-wrap">
                  <i className={`fa-solid ${kpi.iconClass}`} />
                </div>
              </div>
              <p className="ib-dashboard-kpi-label">{t(kpi.labelKey)}</p>
              <p className="ib-dashboard-kpi-value">
                {dashboardLoading ? renderIbSkeleton("ib-dashboard-skeleton--kpi-value") : kpi.valueMain}
                {kpi.valueSub ? <span className="ib-dashboard-kpi-decimals">{kpi.valueSub}</span> : null}
              </p>
            </article>
          ))}
        </div>

        <div
          className={`ib-dashboard-invite-distribution-row ${
            !hasDistributionData ? "ib-dashboard-invite-distribution-row--single" : ""
          }`}
        >
          <section className="ib-dashboard-invite-card" aria-labelledby="ib-invite-card-title">
            <div className="ib-dashboard-invite-layout">
              <div className="ib-dashboard-invite-qr-wrap">
                <IbReferralQRCode
                  value={inviteUrl}
                  size={isMobileLayout ? 220 : 176}
                  title={t("ib_invite_qr_aria")}
                  className="ib-dashboard-invite-qr-img"
                />
              </div>
              <div className="ib-dashboard-invite-copy">
                <h2 id="ib-invite-card-title" className="ib-dashboard-invite-title">
                  {t("ib_invite_card_title")}
                </h2>
                <p className="ib-dashboard-invite-desc">{t("ib_invite_card_desc")}</p>
                <div className="ib-dashboard-referral-row">
                  <div className="ib-dashboard-referral-text-wrap">
                    {editingInviteId ? (
                      <CustomDropdown
                        options={brokerAccountOptions}
                        value={selectedBrokerOption}
                        onChange={(option) => {
                          setInviteBrokerIdInvalid(false);
                          const digits =
                            typeof option === "object" && option !== null
                              ? normalizeInviteAccountIdDigits(option.value)
                              : normalizeInviteAccountIdDigits(option);
                          setInviteAccountIdDigits(digits);
                        }}
                        placeholder={
                          brokerAccountsLoading
                            ? t("loading", { defaultValue: "Loading..." })
                            : t("ib_select_broker_account", {
                                defaultValue: "Select broker account",
                              })
                        }
                        disabled={savingInviteId || brokerAccountsLoading}
                        searchable
                        className={`ib-dashboard-referral-dropdown${
                          inviteBrokerIdInvalid ? " dropdown-error" : ""
                        }`}
                        getDisplayValue={(option) => {
                          if (!option) return "";
                          if (typeof option === "object") return option.label || option.value || "";
                          return String(option);
                        }}
                        aria-invalid={inviteBrokerIdInvalid}
                        aria-label={t("ib_edit_referral_aria")}
                      />
                    ) : (
                      <p className="ib-dashboard-referral-static">
                        <span className="ib-dashboard-referral-base">{informationAccountPrefixDisplay}</span>
                        <span className="ib-dashboard-referral-code">
                          /{inviteAccountIdDigits || "Account number"}
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="ib-dashboard-referral-actions">
                    <button
                      type="button"
                      className="ib-dashboard-referral-btn ib-dashboard-referral-btn--edit"
                      onClick={() => {
                        if (editingInviteId) {
                          void finishEditInviteId();
                        } else {
                          setInviteBrokerIdInvalid(false);
                          setEditingInviteId(true);
                        }
                      }}
                      disabled={savingInviteId}
                      aria-busy={savingInviteId}
                      aria-label={editingInviteId ? t("ib_save_referral_aria") : t("ib_edit_referral_aria")}
                    >
                      <i
                        className={`fa-solid ${
                          savingInviteId
                            ? "fa-spinner fa-spin"
                            : editingInviteId
                              ? "fa-check"
                              : "fa-pen"
                        }`}
                        aria-hidden
                      />
                    </button>
                    {hasInviteAccountId ? (
                      <button
                        type="button"
                        className="ib-dashboard-referral-btn ib-dashboard-referral-btn--copy"
                        onClick={handleCopyReferral}
                        aria-label={t("ib_copy_referral_aria")}
                      >
                        <i className="fa-regular fa-copy" aria-hidden />
                      </button>
                    ) : null}
                    {hasInviteAccountId ? (
                      <button
                        type="button"
                        className="ib-dashboard-referral-btn ib-dashboard-referral-btn--download"
                        onClick={handleDownloadQrCode}
                        aria-label={t("ib_download_qr_aria", { defaultValue: "Download QR code" })}
                      >
                        <i className="fa-solid fa-download" aria-hidden />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {hasDistributionData ? (
          <section className="ib-dashboard-distribution-card" aria-labelledby="ib-client-distribution-title">
            <h2 id="ib-client-distribution-title" className="ib-dashboard-panel-title ib-dashboard-distribution-title">
              {t("ib_client_distribution")}
            </h2>
            <div className="ib-dashboard-distribution-body">
              <div
                className="ib-dashboard-distribution-donut"
                style={{ background: distributionGradient }}
                role="img"
                aria-label={t("ib_client_distribution_chart_aria")}
              >
                <div className="ib-dashboard-distribution-donut-hole" aria-hidden />
              </div>
              <div className="ib-dashboard-distribution-legend">
                {distributionRows.length ? distributionRows.map((row, rowIdx) => (
                  <div key={`legend-row-${rowIdx}`} className="ib-dashboard-distribution-legend-row">
                    {row.map((seg) => {
                      if (!seg) return null;
                      return (
                        <div key={seg.id} className="ib-dashboard-distribution-legend-item">
                          <span
                            className="ib-dashboard-distribution-swatch"
                            style={{ backgroundColor: seg.color }}
                            aria-hidden
                          />
                          <span className="ib-dashboard-distribution-label">
                            {seg.label} ({disp(seg.pct, "0")}%)
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )) : (
                  <div className="ib-dashboard-distribution-legend-row">
                    <div className="ib-dashboard-distribution-legend-item">
                      <span className="ib-dashboard-distribution-label">
                        {dashboardLoading ? renderIbSkeleton("ib-dashboard-skeleton--legend") : "—"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
          ) : null}
        </div>

        <div className="ib-dashboard-clients-split">
          <section className="ib-dashboard-recent-card" aria-labelledby="ib-recent-clients-title">
            <div className="ib-dashboard-recent-head">
              <h2 id="ib-recent-clients-title" className="ib-dashboard-panel-title">
                {t("ib_recent_clients")}
              </h2>
              <div className="ib-dashboard-recent-controls">
                <button type="button" className="ib-dashboard-view-all" onClick={() => navigate("/pages/ibDashboard/clients-list")}>
                  {t("ib_view_all")}
                </button>
              </div>
            </div>

            <div className="accounts-table-container container-fluid px-0 w-100">
            <div className="ib-dashboard-table-wrap">
              <div className="table-responsive accounts-table-scroll w-100">
              <table className="ib-dashboard-table table table-borderless align-middle mb-0 w-100">
                <thead>
                  <tr>
                    <th>{t("ib_col_client_name")}</th>
                    <th>{t("ib_col_status")}</th>
                    <th>{t("ib_col_account_type")}</th>
                    <th>{t("ib_col_equity")}</th>
                    <th className="ib-dashboard-th-action">{t("ib_col_action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardLoading ? (
                    Array.from({ length: RECENT_PAGE_SIZE }).map((_, idx) => (
                      <tr key={`recent-skeleton-${idx}`}>
                        <td><span className="ib-dashboard-skeleton ib-dashboard-skeleton--table-name" /></td>
                        <td><span className="ib-dashboard-skeleton ib-dashboard-skeleton--table-chip" /></td>
                        <td><span className="ib-dashboard-skeleton ib-dashboard-skeleton--table-text" /></td>
                        <td><span className="ib-dashboard-skeleton ib-dashboard-skeleton--table-text" /></td>
                        <td className="ib-dashboard-td-action"><span className="ib-dashboard-skeleton ib-dashboard-skeleton--icon" /></td>
                      </tr>
                    ))
                  ) : paginatedRecent.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="ib-dashboard-table-empty">
                        {t("ib_recent_no_results")}
                      </td>
                    </tr>
                  ) : (
                    paginatedRecent.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <div className="ib-dashboard-cell-user">
                            <span className="ib-dashboard-avatar" aria-hidden>
                              {avatarInitials(row.name)}
                            </span>
                            <div className="ib-dashboard-cell-user-text">
                              <span className="ib-dashboard-name" title={String(row.name || "")}>
                                {isMobileLayout ? disp(row.name) : formatIbClientName(row.name, 13, "—")}
                              </span>
                              {!isMissing(row.email) ? (
                                <span className="ib-clients-id-email-sub">{String(row.email).trim()}</span>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`ib-dashboard-status ib-dashboard-status--${row.status}`}>
                            <span className="ib-dashboard-status-dot" aria-hidden />
                            {t(`ib_status_${row.status}`)}
                          </span>
                        </td>
                        <td className="ib-dashboard-td-muted">
                          {(() => {
                            const normalizedType = String(row.accountType || "")
                              .trim()
                              .toLowerCase()
                              .replace(/\s+/g, "_");
                            if (normalizedType === "large") {
                              return t("ib_tier_large", { defaultValue: "Large" });
                            }
                            if (normalizedType === "mini") {
                              return t("ib_tier_mini", { defaultValue: "Mini" });
                            }
                            if (normalizedType === "standard" || normalizedType === "std") {
                              return t("ib_tier_standard", { defaultValue: "Standard" });
                            }
                            return row.accountType;
                          })()}
                        </td>
                        <td className="ib-dashboard-td-equity">{formatEquity(row.equity)}</td>
                        <td className="ib-dashboard-td-action">
                          <button
                            type="button"
                            className="ib-eye-btn"
                            onClick={() => handleViewRecentClientAccounts(row)}
                            aria-label={t("ib_clients_view_accounts_aria")}
                          >
                            <i className="fa-solid fa-eye" aria-hidden />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
            </div>
            </div>

            <div className="ib-dashboard-table-footer" />
          </section>

          <aside className="ib-dashboard-new-card" aria-labelledby="ib-new-clients-title">
            <div className="ib-dashboard-new-head">
              <h2 id="ib-new-clients-title" className="ib-dashboard-panel-title">
                {t("ib_new_clients")}
              </h2>
              <button type="button" className="ib-dashboard-view-all" onClick={() => navigate("/pages/ibDashboard/clients-list")}>
                {t("ib_view_all")}
              </button>
            </div>
            <ul className="ib-dashboard-new-list">
              {dashboardLoading
                ? Array.from({ length: 4 }).map((_, idx) => (
                    <li key={`new-skeleton-${idx}`} className="ib-dashboard-new-item">
                      <span className="ib-dashboard-skeleton ib-dashboard-skeleton--avatar" />
                      <div className="ib-dashboard-new-item-text">
                        <span className="ib-dashboard-skeleton ib-dashboard-skeleton--name" />
                        <span className="ib-dashboard-skeleton ib-dashboard-skeleton--sub" />
                      </div>
                      <span className="ib-dashboard-skeleton ib-dashboard-skeleton--chip" />
                    </li>
                  ))
                : newClients.map((row) => (
                <li key={row.id} className="ib-dashboard-new-item">
                  <span className="ib-dashboard-avatar ib-dashboard-avatar--sm" aria-hidden>
                    {row.initials || avatarInitials(row.name)}
                  </span>
                  <div className="ib-dashboard-new-item-text">
                    <span className="ib-dashboard-name" title={String(row.name || "")}>
                      {isMobileLayout ? disp(row.name) : formatIbClientName(row.name, 13, "—")}
                    </span>
                    {row.email ? (
                      <span className="ib-dashboard-new-sub">{String(row.email)}</span>
                    ) : null}
                  </div>
                  <span className={`ib-dashboard-tier ib-dashboard-tier--${row.tier}`}>
                    {row.tier === "large"
                      ? t("ib_tier_large", { ns: "mobile", defaultValue: "Large" })
                      : row.tier === "mini"
                      ? t("ib_tier_mini", { ns: "mobile", defaultValue: "Mini" })
                      : t("ib_tier_standard", { ns: "mobile", defaultValue: "Standard" })}
                  </span>
                </li>
              ))}
            </ul>
          </aside>
        </div>
        </div>
      </div>
    </div>
  );
};

export default IbDashboard;
